import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
	Card,
	CardContent,
	ColorThemeImageContainer,
	TopBar
} from '@features/trend-research/components/common/TrendCard';
import { DataSource, Divider, Product } from '@features/trend-research/components/details/components';
import {
	FilterButtons,
	SubCategoryItems
} from '@features/trend-research/components/details/components/Filters';
import ProductTopSection from '@features/trend-research/components/details/Fabric/ProductTopSection';
import useQueryString from '@features/trend-research/hooks/useQueryString';
import {
	areArraysEqual,
	cleanUrl,
	detailsCountHeaders,
	formatFabricName,
	getDetailsCount,
	sourceType
} from '@features/trend-research/utils/utils';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	findBrandInsight,
	findThemeInsight,
	findTrendResearchProductsBySource
} from '@services/trendResearch';

const Theme = () => {
	const dispatch = useDispatch();
	let queryString = useQueryString();

	const [selectedSubCategory, setSelectedSubCategory] = useState([]);
	const location = useLocation();
	const [selectedItem, setSelectedItem] = useState(null);

	const [page, setPage] = useState(0);
	const [newRelatedProducts, setNewRelatedProducts] = useState([]);
	const [queryStringForApi, setQueryStringForApi] = useState('');
	const [detailsCount, setDetailsCount] = useState({});
	const [isClose, setIsClose] = useState(false);
	const [themeData, setThemeData] = useState({});
	const [totalPage, setTotalPage] = useState(0);

	const { source } = location?.state || {};
	//const { data: themeData } = useApi(findThemeInsight, `${sourceType[source]}/theme-list`, queryString);

	const fetchThemeData = async (_queryString) => {
		try {
			dispatch(openLoader());
			const response = await findThemeInsight(`${sourceType[source]}/theme-list`, _queryString);
			setThemeData(response);

			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};
	useEffect(() => {
		if (queryStringForApi !== '') {
			fetchThemeData(queryStringForApi);
		}
	}, [queryStringForApi]);

	useEffect(() => {
		setQueryStringForApi(queryString);
	}, [queryString]);

	const handleSelectionChange = (newSelectedItems) => {
		setSelectedSubCategory(newSelectedItems);
		const newThemeList = newSelectedItems.join(',');

		const searchParams = new URLSearchParams(queryStringForApi);
		if (searchParams.has('themeList')) {
			searchParams.delete('themeList');
		}
		if (newThemeList !== 'All') {
			searchParams.append('themeList', newThemeList);
		}
		const updatedQueryString = `?${searchParams.toString()}`;
		setNewRelatedProducts([]);
		setQueryStringForApi(updatedQueryString);
		setPage(0);
	};

	const fetchDetailsCount = async () => {
		try {
			dispatch(openLoader());
			const response = await findBrandInsight('details-count', queryStringForApi);
			setDetailsCount((prev) => ({
				...prev,
				maxPrice: response?.maxPrice,
				midPrice: response?.midPrice,
				minPrice: response?.minPrice
			}));

			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		window.scrollTo(0, 0);
		if (source !== 'Instagram' && queryStringForApi !== '') {
			fetchDetailsCount(queryStringForApi);
		}
	}, [queryStringForApi]);

	const fetchProducts = async (queryString, offset = 0) => {
		try {
			dispatch(openLoader());
			if (page <= totalPage) {
				const response = await findTrendResearchProductsBySource(
					sourceType[source],
					queryString + `&page=${offset}&size=20`
				);
				if (!response.data.length) {
					dispatch(closeLoader());
					return;
				}
				setTotalPage(response.totalPages);
				setDetailsCount((prev) => ({
					...prev,
					noOfProducts: response?.totalElements
				}));

				setNewRelatedProducts((prevProducts) => [...prevProducts, ...response.data]);
			}
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (queryStringForApi !== '') {
			fetchProducts(queryStringForApi, page);
		}
	}, [page, queryStringForApi]);

	const handleScroll = async () => {
		if (
			window.innerHeight + document.documentElement.scrollTop + 1 >=
			document.documentElement.scrollHeight
		) {
			setPage((prev) => prev + 1);
		}
	};
	useEffect(() => {
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const onClose = () => {
		setIsClose(true);
		const searchParams = new URLSearchParams(queryStringForApi);
		if (searchParams.has('themeList')) {
			searchParams.delete('themeList');
		}

		const updatedQueryString = `?${searchParams.toString()}`;
		setNewRelatedProducts([]);
		setQueryStringForApi(updatedQueryString);
		setPage(0);
		setSelectedSubCategory([]);
	};

	useEffect(() => {
		if (location?.state && location?.state?.selectedItem) {
			setSelectedItem(location.state.selectedItem);
			setSelectedSubCategory(formatFabricName(location?.state?.selectedItem.themeName));
		}
	}, [location?.state?.selectedItem]);

	const [mainImage, ...images] = selectedItem?.imageUrlList?.map(cleanUrl) || [];

	return (
		<>
			<FilterButtons>
				<SubCategoryItems
					items={themeData ? themeData : {}}
					selectedItems={selectedSubCategory}
					onSelectionChange={handleSelectionChange}
					multiple
					isSlideShow
					showAll
					scrollTop={0}
				/>
			</FilterButtons>
			{isClose ||
				(areArraysEqual(
					selectedSubCategory,
					formatFabricName(location?.state?.selectedItem?.themeName)
				) && (
					<div className="key-theme-section trend-padding">
						<div className="key-theme-slider grid-column-4">
							<Card className="single-key-theme-item edit-key-theme-item">
								<TopBar topText={location?.state?.selectedItem?.isTop} onClose={onClose} />
								<ColorThemeImageContainer mainImage={mainImage} images={images} />
								<CardContent
									details={{
										title: selectedItem?.themeName,
										itemCount: selectedItem?.themeCount,
										percentage: selectedItem?.percentage
									}}
								/>
							</Card>
						</div>
					</div>
				))}

			<Divider />
			<div className="trending-product border-0 top-fabric-product">
				<DataSource data={getDetailsCount(detailsCount)} headers={detailsCountHeaders} />
				<ProductTopSection title="Related Products" />

				<Product allProducts={newRelatedProducts} />
			</div>
		</>
	);
};

export default Theme;
